import { Ic } from '../../components/Ic';
import { Gauge } from '../../components/Gauge';
import { StatusPill } from '../../components/StatusDot';
import { deriveScraperState, channelForSite } from '../../lib/scraperState';
import { formatDuration, formatInterval, relTime } from '../../lib/time';
import { ScraperActions } from './ScraperActions';
import type { Scraper } from '../../api/types';

function Metric({ k, v, color }: { k: string; v: string | number; color?: string }) {
  return (
    <div className="metric">
      <div className="k">{k}</div>
      <div className="v" style={color ? { color } : undefined}>
        {v}
      </div>
    </div>
  );
}

export function ScraperCard({
  scraper,
  found,
  today,
  onOpen,
}: {
  scraper: Scraper;
  found: number;
  today: number;
  onOpen: () => void;
}) {
  const state = deriveScraperState(scraper);
  const channel = channelForSite(scraper.name);

  // The design showed CPU/MEM bars. The web container has no cgroup access to
  // the worker, so instead of fabricating them this reuses the same bar for
  // slow_ratio — runtime as a fraction of the interval, which is the real
  // capacity signal and is what slow_warning is computed from.
  const ratio = scraper.slow_ratio;

  return (
    <div className={`scraper ${state === 'down' ? 'down' : ''}`}>
      <div className="sc-head">
        <div className="icon">
          <Ic.box className="ic" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{scraper.name}</h3>
          <div className="sub">
            every {formatInterval(scraper.interval_seconds)}
            {scraper.file ? ` · ${scraper.file}` : ''}
          </div>
        </div>
        <StatusPill state={state} />
      </div>

      <div className="metrics">
        {/* health_score substitutes for the design's fabricated uptime %. */}
        <Metric k="Health" v={scraper.health_score} />
        <Metric k="Found · today" v={`${found} · ${today}`} />
        {/* consecutive_failures substitutes for the design's error rate, which
            nothing in the backend tracks. */}
        <Metric
          k="Fails in a row"
          v={scraper.consecutive_failures || '—'}
          color={scraper.consecutive_failures > 0 ? 'var(--err)' : undefined}
        />
      </div>

      {ratio !== null && state !== 'paused' && (
        <div className="res">
          <div className="rr">
            <span className="rk">Load</span>
            <div className="gauge-wrap" style={{ flex: 1 }}>
              <Gauge
                value={Math.min(ratio * 100, 100)}
                color={scraper.slow_warning ? 'var(--warn)' : 'var(--accent)'}
              />
            </div>
            <span className="rv">{Math.round(ratio * 100)}%</span>
          </div>
          <div className="rr">
            <span className="rk">Avg</span>
            <div style={{ flex: 1 }} />
            <span className="rv">{formatDuration(scraper.avg_execution_time)}</span>
          </div>
        </div>
      )}

      {scraper.error && (
        <div className="err">
          {scraper.error_type && (
            <span className="pill red" style={{ marginRight: 6 }}>
              {scraper.error_type}
            </span>
          )}
          {scraper.error}
        </div>
      )}

      <div className="foot">
        <Ic.clock style={{ width: 13, height: 13, color: 'var(--fg-3)' }} />
        <span style={{ fontSize: 11.5, color: 'var(--fg-3)' }}>
          {scraper.enabled && scraper.next_run
            ? `next run ${relTime(scraper.next_run)}`
            : `last ran ${relTime(scraper.last_run)}`}
        </span>
        <span style={{ flex: 1 }} />
        <span
          className="pill"
          title="Routing is decided by site name inside send_raffles, not per scraper"
        >
          #{channel}
        </span>
      </div>

      <div className="foot" style={{ borderTop: 'none', paddingTop: 0, gap: 8 }}>
        <ScraperActions scraper={scraper} />
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" onClick={onOpen}>
          Details <Ic.chevRight className="ico" />
        </button>
      </div>
    </div>
  );
}
